import {
  GraphLineIcon,
  IconButton,
  RocketIcon,
  SingleSelectDropdown,
  Spinner,
  TargetIcon,
  Text,
  Tooltip,
  toast,
  type DataTableColumn,
} from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { isTrainingPolicyNotFoundError } from "@/api/training";
import {
  getTrainingPolicyDownload,
  isPolicyArtifactUnavailableError,
} from "@/api/trainingPolicies";
import { createTrainingShareLink } from "@/api/trainingShareLinks";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { AxisFilterBar, AxisFilterChips, FilterResultCount } from "@/components/filter";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { DownloadIcon } from "@/components/icons";
import { createLogger } from "@/logging";
import { triggerBrowserDownload } from "@/utils";
import { formatDateTime, formatRelativeTime } from "@/utils/datetime";
import { useRelativeTimeClock } from "@/utils/useRelativeTimeClock";
import {
  EvaluationParamsModal,
  prefetchEvalImageParams,
  type EvaluationTargetPolicy,
} from "./evalParams";
import { PolicyTableRow } from "./PolicyTableRow";
import { DatasetRefCell } from "./DatasetRefCell";
import { readCatalogFilters, type SessionScope } from "@/modules/training/public";
import { PolicyTableSource } from "./PolicyTableSource";
import {
  POLICY_FILTER_FIELDS,
  type PolicyFilterChip,
  type PolicyTextFilters,
} from "./policyFilterFields";
import {
  readPolicyFilterChips,
  readPolicyRegisteredWithin,
  writePolicyFilterChips,
  writePolicyRegisteredWithinParam,
} from "./policyFilterParams";
import {
  ALL_POLICY_REGISTERED_WITHIN,
  POLICY_REGISTERED_WITHIN_OPTIONS,
  policyRegisteredWithinLabel,
  type PolicyRegisteredWithin,
} from "./policyRegisteredWithin";
import { PaginationFooter } from "@/components/browse";
import { TrainingSessionRefLink } from "./TrainingSessionRefLink";
import { useShareLinkIssuer } from "./shareLinks/useShareLinkIssuer";
import { BranchHashCell } from "@/components/table/branchHash";
import { CheckpointCount, isCheckpointSortable } from "@/components/table/CheckpointCount";
import tableStyles from "@/components/table/dataTableSelection.module.css";
import headerStyles from "@/components/table/tablePanelHeader.module.css";
import styles from "./TrainingPoliciesPanel.module.css";

const logger = createLogger("page/training/policiesPanel");

// 이 탭의 초기 페이지당 행 수. 실행 액션과 페이저가 화면에 보이도록 기본값을 작게 둔다. 큰
// 카탈로그를 훑을 때는 페이저의 Rows 컨트롤로 더 큰 크기를 고른다.
export const DEFAULT_EVALUATION_PAGE_SIZE = 20;

// 정책 source 를 만드는 한 가지 방법. Evaluation 페이지가 인스턴스를 소유하고 패널에 주입한다.
// 그래서 패널이 다시 마운트되어도 선택이 남는다.
export function createPolicyTableSource({
  scope,
  onRowActivate,
  memberUuid,
  experimentId,
  textFilters,
  registeredWithin,
  selectedPolicyIds,
  pageSize,
}: {
  scope: SessionScope;
  // 행 클릭 활성화. 페이지가 여기서 정책을 선택하고 결과 창을 연다.
  onRowActivate?: (row: PolicyTableRow) => void;
  memberUuid: string;
  experimentId: string;
  textFilters?: PolicyTextFilters;
  registeredWithin?: PolicyRegisteredWithin;
  // URL 이 준 체크박스 선택(?policy= 반복).
  selectedPolicyIds?: readonly string[];
  // 페이지당 행 수. 페이지가 actor 에게 고르게 하고 현재 값을 전달한다. 그래서 첫 로드부터 이
  // 값을 쓴다.
  pageSize: number;
}): PolicyTableSource {
  return new PolicyTableSource({
    scope,
    pageSize,
    resolveErrorMessage: resolvePoliciesError,
    // 정책 선택은 실행 모달을 열 의도를 클릭보다 몇 초 먼저 알린다. Run Evaluation 버튼은 그
    // 전까지 disabled 라서 hover 로 더 일찍 준비할 수 없다. api 계층 캐시 덕분에 여러 번 호출해도
    // 결과가 같다. 그래서 토글마다 호출해도 비용이 없다.
    onSelectionChange: () => prefetchEvalImageParams(),
    onRowActivate,
    // 첫 렌더의 URL 필터를 seed 로 넣는다. 그래서 딥링크의 첫 조회부터 필터가 적용된다
    // (rules §7).
    initialMemberUuid: memberUuid,
    initialExperimentId: experimentId,
    initialTextFilters: textFilters,
    initialRegisteredWithin: registeredWithin,
    initialSelectedPolicyIds: selectedPolicyIds,
  });
}

// Evaluation 페이지의 정책 카탈로그 표. My(scope=me)와 Team(scope=team) 스코프가 함께 쓴다.
// 정책은 세션의 체크포인트 탭에서 등록한다.
//
// source 는 생성자에서 스코프를 고정한다. 그래서 스코프가 바뀌면 호출부가 패널을 다시 마운트한다.
//
// queryControls 는 페이지가 소유한 축(그룹·멤버·experiment)이다. 이 축들이 들어가는 행은 이 패널이
// 소유한다. 이 패널의 자체 컨트롤은 행 앞쪽의 검색 바와 행 끝의 등록 기간 드롭다운이다.
//
// 페이지가 주입된 source 를 소유한다. 생성, initialize()/dispose(), URL 과 그룹 동기화를 페이지가
// 한다. 그래서 패널이 다시 마운트되어도 선택이 남고, 패널은 렌더만 한다. 행 클릭은 정책의
// Evaluation Results 창을 연다(source onRowActivate). 선택은 체크박스가 소유한다.
export function TrainingPoliciesPanel({
  scope,
  onPageSizeChange,
  queryControls,
  source,
  onViewEvaluationResults,
  onCompareSelectedPolicies,
}: {
  scope: SessionScope;
  // 페이지당 행 수 선택. 선호 값은 페이지가 소유한다. 이 값은 스코프 key 로 다시 마운트해도 남아야
  // 한다. 페이지는 주입한 source 에 쓰는 유일한 주체다. 그래서 패널은 source 크기를 직접 바꾸지
  // 않고 선택을 알린다.
  onPageSizeChange: (pageSize: number) => void;
  queryControls?: ReactNode;
  source: PolicyTableSource;
  // 체크한 정책으로 거른 Evaluation Results 전체 화면 창을 연다.
  onViewEvaluationResults: () => void;
  // 체크한 정책의 비교 패널을 연다. 비동기다. 페이지가 먼저 각 정책의 eval run 을 조회하고, 그동안
  // 패널은 진행 상태를 표시한다.
  onCompareSelectedPolicies: () => Promise<void>;
}) {
  // 검색 칩은 URL(?policy_*=)에 있다. 그래서 탭 전환, 새로고침, 딥링크 뒤에도 남는다. 위의 id 값
  // 조회 축과 달리 스코프에 의존하지 않는다. 그래서 스코프나 그룹이 바뀌어도 남는다.
  const [searchParams, setSearchParams] = useSearchParams();
  const filterChips = useMemo(() => readPolicyFilterChips(searchParams), [searchParams]);
  // 등록일 기간 퀵 필터도 검색 칩과 같이 URL(?policy_registered=)이 보관한다. 스코프와 무관한 축이다.
  const registeredWithin = readPolicyRegisteredWithin(searchParams);
  // 검색 칩·기간·페이지 축 중 하나라도 좁혀졌으면 빈 결과의 이유는 "아직 없음"이 아니다.
  const hasQueryFilters =
    filterChips.length > 0 ||
    registeredWithin !== ALL_POLICY_REGISTERED_WITHIN ||
    Object.keys(readCatalogFilters(searchParams, scope)).length > 0;

  const handleFilterChipsChange = useCallback(
    (next: readonly PolicyFilterChip[]) => {
      setSearchParams((prev) => writePolicyFilterChips(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const handleRegisteredWithinChange = useCallback(
    (next: string) => {
      setSearchParams((prev) => writePolicyRegisteredWithinParam(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  // 등록일 프리셋은 검색 칩과 같은 행에 칩으로 표시한다. ✕ 는 전체로 되돌린다. 초기화는 검색 칩과
  // 등록일을 한 번의 URL 커밋으로 비운다.
  const registeredChips = useMemo(
    () =>
      registeredWithin === ALL_POLICY_REGISTERED_WITHIN
        ? []
        : [
            {
              key: "registered",
              label: `등록일: ${policyRegisteredWithinLabel(registeredWithin)}`,
              removeLabel: `등록일: ${policyRegisteredWithinLabel(registeredWithin)} 해제`,
              onRemove: () =>
                setSearchParams(
                  (prev) => writePolicyRegisteredWithinParam(prev, ALL_POLICY_REGISTERED_WITHIN),
                  { replace: true },
                ),
            },
          ],
    [registeredWithin, setSearchParams],
  );
  const handleClearAllFilters = useCallback(() => {
    setSearchParams(
      (prev) =>
        writePolicyRegisteredWithinParam(
          writePolicyFilterChips(prev, []),
          ALL_POLICY_REGISTERED_WITHIN,
        ),
      { replace: true },
    );
  }, [setSearchParams]);

  // 실행 모달. 열 때 선택 행의 스냅샷을 만든다. 그래서 모달이 열린 동안 페이지를 넘겨도 평가
  // 대상이 바뀌지 않는다.
  const [evalTargets, setEvalTargets] = useState<readonly EvaluationTargetPolicy[] | null>(null);

  const openEvaluation = useCallback(() => {
    setEvalTargets(
      source.getSelectedRows().map((row) => ({
        id: row.getRowId(),
        name: row.name,
        epoch: row.epochValue,
        budgetUnit: row.budgetUnit,
        datasetLabel: row.datasetLabel,
        // TASK 매칭에는 위의 placeholder 라벨이 아니라 registry 이름이 필요하다.
        datasetName: row.datasetRef?.display_name ?? null,
        createdAtMs: row.createdAtMs,
      })),
    );
  }, [source]);

  const handleEvaluated = useCallback(() => {
    source.clearSelection();
  }, [source]);

  // 조회하는 동안 다시 클릭을 막고 진행 표시(스피너·캡션)를 표시한다. 성공·실패 모두 finally 에서 해제한다.
  const [comparePending, setComparePending] = useState(false);
  const handleCompare = useCallback(async () => {
    if (comparePending) {
      return;
    }
    setComparePending(true);
    try {
      await onCompareSelectedPolicies();
    } catch {
      // 실패 안내는 콜백 소유자(페이지의 토스트)가 맡는다. 여기서는 진행 표시만 정리한다.
    } finally {
      setComparePending(false);
    }
  }, [comparePending, onCompareSelectedPolicies]);

  // 공유는 발급 기록이 남는 링크를 만든다. 복사·클립보드 실패·오류 문구는 공용 훅이 소유한다.
  const shareIssuer = useShareLinkIssuer();
  const { issue: issueShareLink } = shareIssuer;
  const handleShareLink = useCallback(
    (row: PolicyTableRow) =>
      void issueShareLink(row.id, () =>
        createTrainingShareLink({ kind: "policy", policy_id: row.id }),
      ),
    [issueShareLink],
  );

  // 다운로드는 요청자가 바로 받는 1시간 링크라 공유 목록에 남지 않는다. 정책 1건 = 파일 1개라
  // 세션 표면과 달리 고를 것이 없다. 그래서 팝업 없이 바로 받는다. 링크는 다른 origin(storage)이고
  // 체크포인트는 크기 상한이 없다. 그래서 blob 으로 받지 않는다.
  const [downloadingPolicyId, setDownloadingPolicyId] = useState<string | null>(null);
  const handleDownload = useCallback(
    async (row: PolicyTableRow) => {
      if (downloadingPolicyId !== null) {
        return;
      }
      setDownloadingPolicyId(row.id);
      try {
        const link = await getTrainingPolicyDownload(row.id);
        triggerBrowserDownload(link.download_url, { filename: link.filename, crossOrigin: true });
      } catch (error) {
        logger.error("policy download url failed", error);
        toast.error(policyDownloadErrorMessage(error));
      } finally {
        setDownloadingPolicyId(null);
      }
    },
    [downloadingPolicyId],
  );

  const rowActions = useMemo<PolicyRowActions>(
    () => ({
      onShare: handleShareLink,
      sharingPolicyId: shareIssuer.pendingKey,
      onDownload: (row) => void handleDownload(row),
      downloadingPolicyId,
    }),
    [handleShareLink, shareIssuer.pendingKey, handleDownload, downloadingPolicyId],
  );

  const nowMs = useRelativeTimeClock();
  // 로딩과 페이지 변경을 구독한다. StrictMode remount 뒤 다시 활성화되는 경우도 포함한다.
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const checkpointSortable = isCheckpointSortable(
    snapshot.rows,
    source.sortedColumnKey === "epochLabel",
  );
  const columns = useMemo(
    () => buildPolicyColumns(nowMs, checkpointSortable, rowActions),
    [nowMs, checkpointSortable, rowActions],
  );
  const selectedCount = snapshot.selectedIds.size;
  const isLoading = snapshot.loadState === "loading";

  return (
    <section className={styles.panel} aria-label="Policy 목록">
      {/* 조회 컨트롤(1줄)과 필터 칩(2줄)은 선택 중에도(URL 로 복원한 선택 포함) 조건을 바꿀 수
          있어야 한다. 그래서 선택 여부와 무관하게 자리를 유지한다. 선택 액션은 표 위 도구 띠(BulkActionBar)에 있다. */}
      <div className={headerStyles.toolbar}>
        <div className={headerStyles.filters}>
          {/* 검색 묶음이 행의 앞이다(rules.md §17, 검색이 행 1 맨 앞). 구분선 뒤가 페이지 축이다. */}
          <AxisFilterBar
            fields={POLICY_FILTER_FIELDS}
            chips={filterChips}
            onChange={handleFilterChipsChange}
          />
          <span className={headerStyles.divider} aria-hidden="true" />
          {queryControls}
          {/* 등록일 프리셋. 세션 목록의 학습 시작일과 같은 facet 드롭다운과 어휘를 쓴다. 전체는
              neutralValue 라 강조가 꺼진다. 어휘는 policyRegisteredWithin 이 소유한다. */}
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.filter}
            label="등록일"
            neutralValue={ALL_POLICY_REGISTERED_WITHIN}
            value={registeredWithin}
            options={POLICY_REGISTERED_WITHIN_OPTIONS}
            onValueChange={handleRegisteredWithinChange}
          />
        </div>
        <AxisFilterChips
          fields={POLICY_FILTER_FIELDS}
          chips={filterChips}
          onChange={handleFilterChipsChange}
          leadingChips={registeredChips}
          onClearAll={handleClearAllFilters}
        />
      </div>
      {/* host: 표 상단 도구 띠(액션·결과 수/선택 개수·해제)를 표와 한 요소로 묶는다(세션 표와
          같은 구조, rules.md §17). 자식은 띠와 표 두 개뿐이다. */}
      <div className={`${styles.tableArea} ${tableStyles.selectionColumn} ${bulkBarStyles.host}`}>
        <BulkActionBar
          source={source}
          ariaLabel="Policy 목록 도구"
          // 결과 수는 조건이 아니라 그 결과다. 그래서 조건 줄이 아니라 여기에 둔다. 선택이 생기면
          // 같은 자리에 개수 문구가 온다(BulkActionBar 계약).
          idleContent={
            snapshot.loadState === "loaded" ? (
              <FilterResultCount count={snapshot.total} unitLabel="Policy" />
            ) : null
          }
          // 자리는 항상 유지하고 활성 상태만 선택 수를 따른다.
          actions={
            <>
              <IconButton
                text="Evaluation 실행"
                type="button"
                size="sm"
                variant="solid"
                disabled={selectedCount === 0}
                title={selectedCount === 0 ? "먼저 Policy 를 하나 이상 선택해 주세요." : undefined}
                onClick={openEvaluation}
              >
                <RocketIcon />
              </IconButton>
              <IconButton
                text="Evaluation 결과 보기"
                type="button"
                size="sm"
                variant="solid"
                disabled={selectedCount === 0}
                title={selectedCount === 0 ? "먼저 Policy 를 하나 이상 선택해 주세요." : undefined}
                onClick={onViewEvaluationResults}
              >
                <TargetIcon />
              </IconButton>
              <IconButton
                text="비교"
                type="button"
                size="sm"
                variant="solid"
                disabled={selectedCount < 2 || comparePending}
                title={
                  selectedCount < 2 ? "비교하려면 Policy 를 두 개 이상 선택해 주세요." : undefined
                }
                onClick={() => void handleCompare()}
              >
                <GraphLineIcon />
              </IconButton>
              {comparePending ? (
                <>
                  {/* label 은 보이는 캡션과 같은 문자열이다. 스크린리더가 두 문구를 읽지 않게 한다. */}
                  <Spinner size="sm" label="Evaluation 실행을 불러오는 중입니다…" />
                  <Text
                    variant={hatcheryTextModes["caption-normal"]}
                    className={styles.compareProgress}
                  >
                    Evaluation 실행을 불러오는 중입니다…
                  </Text>
                </>
              ) : null}
            </>
          }
          // 새로고침(띠 우측, sm, rules.md §17).
          rightContent={
            <IconButton
              size="sm"
              text={isLoading ? "로딩 중…" : "새로고침"}
              variant="surface"
              disabled={isLoading}
              onClick={() => void source.reload()}
            />
          }
        />
        <HatDataTable
          source={source}
          columns={columns}
          emptyMessage={emptyPolicyMessage(scope, hasQueryFilters)}
          loadingMessage="Policy 를 불러오는 중입니다."
          ariaLabel="Policy 목록"
          scrollable
          minWidth="0px"
          showCheckbox
          // c6 DataTable 의 showIndex 기본값은 true 다. 앞쪽 칸은 체크박스 열이 소유한다.
          showIndex={false}
        />
      </div>
      <PaginationFooter
        page={snapshot.page}
        size={snapshot.size}
        total={snapshot.total}
        isLoading={snapshot.loadState === "loading"}
        ariaLabel="Policy 목록 페이지 이동"
        mode="compact"
        hideWhenSinglePage
        // 현재 크기는 source 에서 온다. 그래서 피커와 표의 값이 다를 수 없다.
        onPageSizeChange={onPageSizeChange}
        onPrevious={() => void source.goToPage(Math.max(1, snapshot.page - 1))}
        onNext={() => void source.goToPage(snapshot.page + 1)}
      />
      {evalTargets !== null ? (
        <EvaluationParamsModal
          open
          policies={evalTargets}
          onClose={() => setEvalTargets(null)}
          onSubmitted={handleEvaluated}
        />
      ) : null}
      {shareIssuer.revealModal}
    </section>
  );
}

// 필터 때문에 빈 결과와 정책이 없는 스코프를 구분한다. 안내가 다르다. "아직 없음" 안내는
// 이미 있는 정책을 다시 등록하러 가게 만든다.
function emptyPolicyMessage(scope: SessionScope, hasFilters: boolean): string {
  if (hasFilters) {
    return "조건에 맞는 Policy 가 없습니다. Filter 를 지우고 다시 시도해 보세요.";
  }
  return scope === "me"
    ? "평가할 수 있는 내 Policy 가 없습니다."
    : "이 Team 범위에 평가할 수 있는 Policy 가 없습니다.";
}

// 행 액션(다운로드·공유).
type PolicyRowActions = {
  onShare: (row: PolicyTableRow) => void;
  sharingPolicyId: string | null;
  onDownload: (row: PolicyTableRow) => void;
  downloadingPolicyId: string | null;
};

function policyDownloadErrorMessage(error: unknown): string {
  if (isPolicyArtifactUnavailableError(error)) {
    return "이 Policy는 받을 수 없습니다. 파일이 Training 산출물 저장 위치 밖에 있거나 더 이상 남아 있지 않습니다.";
  }
  if (isTrainingPolicyNotFoundError(error)) {
    return "Policy를 찾을 수 없습니다. 목록을 새로고침해 주세요.";
  }
  return "다운로드 링크를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

// 행 클릭은 결과 창을 연다. 그래서 액션 클릭이 행 활성화로 전파되지 않게 막는다.
function stopRowClick(event: ReactMouseEvent<HTMLElement>): void {
  event.stopPropagation();
}

// 컬럼 정의를 안정적으로 유지한다. 각 key 는 실제 PolicyTableRow 필드와 일치해야 한다.
// 폭이 정해진 컬럼(#, Unit, EVALS, SR[%], Registered)은 고정 px 트랙을 쓴다. 폭이 정해지지 않은
// 식별자 컬럼은 남은 공간을 fr 비율로 나눈다. 최소 폭의 합이 grid min-width 가 된다. 그래서 좁은
// viewport 에서는 모든 컬럼을 줄이지 않고 가로로 스크롤한다.
function buildPolicyColumns(
  nowMs: number,
  checkpointSortable: boolean,
  actions: PolicyRowActions,
): readonly DataTableColumn<PolicyTableRow>[] {
  return [
    {
      key: "name",
      header: "Policy 이름",
      width: "minmax(220px, 2fr)",
      sortable: "both",
      // 셀 글자는 정책 이름이다. 링크는 그 정책을 만든 학습 세션을 체크포인트 탭의 그 정책 행으로
      // 연다. Evaluation Results 의 첫 열과 같은 계약이다. 창 제목 seed 는 세션 이름이어야 하므로
      // 따로 전달한다. 이름을 모르는 세션은 전달하지 않는다. 그러면 상세 응답이 도착할 때 제목을
      // 고친다.
      render: (row) => (
        <TrainingSessionRefLink
          sessionId={row.sourceSessionId}
          displayName={row.name}
          sessionName={row.sessionName ?? undefined}
          focus={{ tab: "checkpoints", checkpoint: row.checkpointTag }}
          className={styles.nameText}
        />
      ),
    },
    {
      key: "experimentLabel",
      header: "실험",
      width: "minmax(140px, 1fr)",
      sortable: "both",
    },
    // 같은 데이터셋에서 나온 정책들을 구분하는 축이라 계보 열(이름·실험) 뒤에 둔다. 뒤따르는
    // #·UNIT 은 그 정책 안의 눈금이다. 그래서 계보 열이 끝난 뒤 숫자 열이 온다.
    {
      key: "branchHashLabel",
      header: "Branch@Hash",
      width: "minmax(140px, 1fr)",
      sortable: "both",
      render: (row) => (
        <BranchHashCell branch={row.branch} hash={row.shortHash} fullHash={row.fullSha} />
      ),
    },
    {
      key: "epochLabel",
      header: "#",
      // 헤더와 정렬 표시가 말줄임 없이 들어가는 폭.
      width: "70px",
      align: "center",
      className: styles.num,
      // 페이지에 예산 단위가 섞이면 헤더를 접는다. 한 숫자 축이 두 뜻을 가지기 때문이다.
      sortable: checkpointSortable ? "both" : "none",
      // 체크포인트 수는 정책의 정체다(이름에 포함된다). 세션 합계는 hover 로 볼 수 있다. 그래서
      // 이 표에서도 학습 규모 정보가 사라지지 않는다.
      render: (row) => (
        <CheckpointCount
          value={row.epochValue}
          unit={row.budgetUnit}
          sessionTotalEpochs={row.sessionEpochValue}
          sessionTotalSteps={row.sessionStepValue}
        />
      ),
    },
    {
      key: "unitLabel",
      header: "UNIT",
      width: "70px",
      align: "center",
    },
    {
      key: "datasetLabel",
      header: "Dataset",
      width: "minmax(320px, 2.2fr)",
      cellPadding: "tight",
      render: (row) => (
        <DatasetRefCell registryRef={row.datasetRef} fallbackLabel={row.datasetLabel} />
      ),
    },
    // 평가 결과 요약 두 열. 정책의 정체(계보·눈금·데이터셋) 뒤, 소유·등록 메타 앞에 둔다.
    // "이 정책이 무엇인가" 다음에 "얼마나 평가했고 얼마나 성공했나" 가 온다.
    {
      key: "evalRunCountLabel",
      header: "EVALS",
      width: "70px",
      align: "center",
      className: styles.num,
      sortable: "both",
    },
    {
      key: "successRateLabel",
      header: "SR[%]",
      width: "80px",
      align: "center",
      className: styles.num,
      sortable: "both",
      // Results 표의 SR[%] 와 같은 강조를 값 요소에 적용한다. 셀 className 은 c6 Text 의 inline
      // weight 보다 우선하지 못한다. hover 는 평균의 분모(점수가 있는 run 수)를 표시한다.
      render: (row) => (
        <Tooltip content={successRateTooltip(row)}>
          <span className={styles.srValue}>{row.successRateLabel}</span>
        </Tooltip>
      ),
    },
    {
      key: "ownerLabel",
      header: "소유자",
      // 폭을 고정한다. 그래서 남은 폭은 식별자 컬럼이 쓰고 Dataset 과 Registered 사이에 빈 공간이
      // 생기지 않는다.
      width: "105px",
      sortable: "both",
    },
    {
      key: "createdLabel",
      header: "등록일",
      // 짧은 상대 시간. 절대 날짜는 hover 로 볼 수 있다.
      width: "110px",
      sortable: "both",
      render: (row) => (
        <Tooltip content={formatEvaluationDateTime(row.createdAtMs)}>
          <span>{formatRelativeTime(row.createdAtMs, nowMs)}</span>
        </Tooltip>
      ),
    },
    {
      // key 는 행 프로퍼티만 쓸 수 있으므로 다른 열이 쓰지 않는 id 를 쓴다. 받기를 공유 왼쪽에
      // 둔다. 정책을 실제로 쓰는 경우가 더 많다.
      key: "id",
      header: "",
      width: "116px",
      align: "right",
      render: (row) => (
        <span className={styles.rowActions} role="presentation" onClick={stopRowClick}>
          <IconButton
            aria-label="다운로드"
            type="button"
            size="sm"
            variant="surface"
            disabled={actions.downloadingPolicyId !== null}
            title="이 Policy 파일을 바로 받습니다."
            onClick={() => actions.onDownload(row)}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            text="공유"
            type="button"
            size="sm"
            variant="surface"
            disabled={actions.sharingPolicyId !== null}
            title="로그인 없이 받을 수 있는 1일짜리 공유 링크를 복사합니다."
            onClick={() => actions.onShare(row)}
          />
        </span>
      ),
    },
  ];
}

// 평균이 있을 때만 분모를 표시한다. 값이 없는 셀에 대시 툴팁을 달지 않는다(rules §8).
function successRateTooltip(row: PolicyTableRow): string | undefined {
  if (row.successRate === null || row.scoredRunCount === null || row.evalRunCount === null) {
    return undefined;
  }
  return `Mean over ${row.scoredRunCount} scored of ${row.evalRunCount} runs`;
}

function resolvePoliciesError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Policy 목록을 불러오지 못했습니다.";
}

// 툴팁 내용용이라 값이 없으면 빈 칸 문자가 아니라 undefined 를 반환한다. content 가 비면 c6 가
// 툴팁을 표시하지 않는다. 대시만 있는 툴팁은 정보가 없고 방해만 된다.
function formatEvaluationDateTime(timestamp: number | null): string | undefined {
  return timestamp === null ? undefined : formatDateTime(timestamp);
}
